/**
 * lineBag.js - saying something different every time.
 *
 * PLATFORM SCAFFOLD. Section 8.5's rule, in general form. The lines themselves
 * belong to a game; drawing them without replacement does not.
 *
 * PURE and seeded.
 *
 * ---------------------------------------------------------------------------
 * WHY A BAG AND NOT Math.random()
 *
 * Section 8.5 says never repeat the same praise twice in a row. Block Builder
 * picks with a bare Math.random() over 50 phrases and has no anti-repeat at all
 * (audit section 5.3); with 50 phrases, chance alone repeats one back-to-back
 * every fiftieth success and produces a visible cluster far more often.
 *
 * A shuffle bag is strictly better than the rule it is asked to satisfy: draw
 * without replacement until the pool is exhausted, then reshuffle. Nothing
 * repeats until everything else has been said, and the reshuffle is nudged so
 * the first line of a new bag is never the last line of the old one.
 *
 * ---------------------------------------------------------------------------
 * TONE
 *
 * Lines carry `tone`. A child who has just needed three prompts does not want
 * to be shouted at, and a child who is flying should not be met with a murmur,
 * so next() takes a preference and honours it when the bag still holds one.
 * It never re-orders the bag beyond that: the no-repeat guarantee comes first.
 */

import { shuffle } from './rounds.js';

/**
 * @param {Array<{text:string, tone?:string}>|string[]} pool
 * @param {object} [opts]
 * @param {() => number} [opts.rng]
 * @returns {{next: (prefer?:string) => string, remaining: () => number,
 *            lastText: () => string|null, size: number}}
 */
export function createLineBag(pool, opts = {}) {
  const rng = opts.rng || Math.random;
  const items = pool.map((entry) => (typeof entry === 'string' ? { text: entry, tone: 'calm' } : entry));

  if (!items.length) throw new Error('createLineBag needs a non-empty pool.');

  let bag = [];
  let last = null;

  const refill = () => {
    bag = shuffle(items, rng);
    // Do not open a new bag with the line the old one closed on. next() also
    // guards this; the swap keeps the bag order itself honest.
    if (bag.length > 1 && last !== null && bag[0].text === last) {
      [bag[0], bag[1]] = [bag[1], bag[0]];
    }
  };

  refill();

  return {
    size: items.length,

    /**
     * @param {'bright'|'calm'} [prefer] tone to prefer, if the bag has one left
     * @returns {string}
     */
    next(prefer) {
      if (!bag.length) refill();

      // Eligibility first, preference second. The no-repeat guarantee is the
      // one section 8.5 actually states, and a tone preference must never be
      // able to reach past it - which it could, when a refill puts the line
      // just used back in the bag and the preference happens to select it.
      const eligible = bag.length > 1
        ? bag.map((_, i) => i).filter((i) => bag[i].text !== last)
        : bag.map((_, i) => i);
      const pool = eligible.length ? eligible : bag.map((_, i) => i);

      let index = pool[0];
      if (prefer) {
        const found = pool.find((i) => bag[i].tone === prefer);
        if (found !== undefined) index = found;
      }

      const [line] = bag.splice(index, 1);
      last = line.text;
      return line.text;
    },

    remaining() { return bag.length; },
    lastText() { return last; },
  };
}

/**
 * A set of bags, one per pool, sharing a seed.
 * @param {Object<string, Array>} pools
 * @param {object} [opts]
 * @returns {Object<string, ReturnType<createLineBag>>}
 */
export function createBags(pools, opts = {}) {
  const out = {};
  for (const [name, pool] of Object.entries(pools)) {
    if (pool && pool.length) out[name] = createLineBag(pool, opts);
  }
  return out;
}

export default { createLineBag, createBags };
